import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { apiGet, apiPost } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { Button } from '@/components/ui/Button';
import { Card, SectionHeader } from '@/components/ui/Card';
import { Input, Textarea } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { ErrorState, LoadingState } from '@/components/ui/States';

interface Settings {
  business_name?: string;
  legal_name?: string;
  phone?: string;
  email?: string;
  gstin?: string;
  pan?: string;
  business_type?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  state_code?: string;
  pincode?: string;
  language?: string;
}

/** Business profile + GST. Uses the same `/settings` endpoint Flutter does;
 *  saves are accumulative (POST any subset). */
export function ProfilePage() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['settings'],
    queryFn: () => apiGet<Settings>(endpoints.settings),
  });

  const [form, setForm] = useState<Settings>({});
  useEffect(() => {
    if (query.data) setForm(query.data);
  }, [query.data]);

  const saveMut = useMutation({
    mutationFn: (next: Settings) => apiPost(endpoints.settings, next),
    onSuccess: () => {
      toast.success('Saved');
      qc.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (query.isLoading) return <LoadingState />;
  if (query.isError) {
    return (
      <ErrorState
        title="Couldn't load profile"
        description={(query.error as Error)?.message}
        onRetry={() => query.refetch()}
      />
    );
  }

  const set = <K extends keyof Settings>(k: K, v: Settings[K]) =>
    setForm({ ...form, [k]: v });

  return (
    <>
      <PageHeader
        back
        title="Business profile"
        description="What customers see on bills, what your CA needs for GST."
        actions={
          <Button
            loading={saveMut.isPending}
            onClick={() => saveMut.mutate(form)}
          >
            Save changes
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <SectionHeader title="Identity" />
          <div className="space-y-3">
            <Input
              name="business_name"
              label="Shop name"
              value={form.business_name ?? ''}
              onChange={(e) => set('business_name', e.target.value)}
            />
            <Input
              name="legal_name"
              label="Legal name (optional)"
              value={form.legal_name ?? ''}
              onChange={(e) => set('legal_name', e.target.value)}
              hint="If different from the shop name on bills."
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                name="phone"
                label="Shop phone"
                value={form.phone ?? ''}
                onChange={(e) => set('phone', e.target.value)}
              />
              <Input
                name="email"
                label="Email"
                type="email"
                value={form.email ?? ''}
                onChange={(e) => set('email', e.target.value)}
              />
            </div>
          </div>
        </Card>

        <Card>
          <SectionHeader title="GST" />
          <div className="space-y-3">
            <Input
              name="gstin"
              label="GSTIN"
              value={form.gstin ?? ''}
              onChange={(e) => set('gstin', e.target.value.toUpperCase())}
              hint="15 chars, e.g. 27AABCU9603R1ZJ"
            />
            <Input
              name="pan"
              label="PAN"
              value={form.pan ?? ''}
              onChange={(e) => set('pan', e.target.value.toUpperCase())}
              hint="10 chars"
            />
            <Input
              name="business_type"
              label="Business type"
              value={form.business_type ?? ''}
              onChange={(e) => set('business_type', e.target.value)}
              hint="e.g. furniture, mobile, kirana, electronics"
            />
          </div>
        </Card>

        <Card className="lg:col-span-2">
          <SectionHeader title="Address" />
          <div className="space-y-3">
            <Textarea
              name="address_line1"
              label="Address line 1"
              rows={2}
              value={form.address_line1 ?? ''}
              onChange={(e) => set('address_line1', e.target.value)}
            />
            <Input
              name="address_line2"
              label="Address line 2 (optional)"
              value={form.address_line2 ?? ''}
              onChange={(e) => set('address_line2', e.target.value)}
            />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Input
                name="city"
                label="City"
                value={form.city ?? ''}
                onChange={(e) => set('city', e.target.value)}
              />
              <Input
                name="state"
                label="State"
                value={form.state ?? ''}
                onChange={(e) => set('state', e.target.value)}
              />
              <Input
                name="state_code"
                label="State code"
                maxLength={2}
                value={form.state_code ?? ''}
                onChange={(e) => set('state_code', e.target.value)}
              />
              <Input
                name="pincode"
                label="Pincode"
                value={form.pincode ?? ''}
                onChange={(e) => set('pincode', e.target.value)}
              />
            </div>
          </div>
        </Card>
      </div>
    </>
  );
}
